class Dialect {
	lineterminator = "\n";
	delimiter = ",";
	quotechar = '"';
	doublequote = true;
	skipinitialspace = true;
	skipinitialrows = 0;
}

const regexForInt = /^\d+$/,
	regexForFloat = /^\d*\.\d+$|^\d+\.\d*$/;

function chomp(s: string, lineterminator: string) {
	if (s.charAt(s.length - lineterminator.length) !== lineterminator) {
		// Does not end with \n, just return string
		return s;
	} else {
		// Remove the \n
		return s.substring(0, s.length - lineterminator.length);
	}
}

function normalizeLineTerminator(
	csvString: string,
	dialect?: Partial<Dialect>
) {
	// Try to guess line terminator if it's not provided.
	if (!dialect?.lineterminator) {
		return csvString.replace(/(\r\n|\n|\r)/gm, "\n");
	}
	// if not return the string untouched.
	return csvString;
}

/**
 * This function takes a string(ideally a line of csv file) and tries to guess the separator type of the file
 */
function guessDelimiter(input: string) {
	let bestDelim, bestDelta, fieldCountPrevRow, maxFieldCount;

	const delimitersToGuess = [",", "\t", "|", ";"];

	for (let i = 0; i < delimitersToGuess.length; i++) {
		const delim = delimitersToGuess[i];
		let delta = 0,
			avgFieldCount = 0;
		fieldCountPrevRow = undefined;

		const preview = parse(input, { delimiter: delimitersToGuess[i] });

		for (let j = 0; j < preview.length; j++) {
			const fieldCount = preview[j].length;
			avgFieldCount += fieldCount;

			if (typeof fieldCountPrevRow === "undefined") {
				fieldCountPrevRow = fieldCount;
				continue;
			} else if (fieldCount > 0) {
				delta += Math.abs(fieldCount - fieldCountPrevRow);
				fieldCountPrevRow = fieldCount;
			}
		}

		if (
			(typeof bestDelta === "undefined" || delta <= bestDelta) &&
			(typeof maxFieldCount === "undefined" ||
				avgFieldCount > maxFieldCount) &&
			avgFieldCount > 1.99
		) {
			bestDelta = delta;
			bestDelim = delim;
			maxFieldCount = avgFieldCount;
		}
	}

	return bestDelim;
}

/**
 * This function is part of csv.js -> https://github.com/okfn/csv.js/tree/master
 */
export function parse(
	data: string | null | undefined,
	dialect?: Partial<Dialect>
) {
	if (!data?.length) return [];
	// When line terminator is not provided then we try to guess it
	// and normalize it across the file.
	if (!dialect || (dialect && !dialect.lineterminator)) {
		data = normalizeLineTerminator(data, dialect);
	}

	const options = {
		...new Dialect(),
		...dialect
	};
	if (!dialect?.delimiter) {
		// only checking the first line in the file
		const delimiter = guessDelimiter(data.slice(0, data.indexOf("\n")));
		options.delimiter = delimiter ?? options.delimiter;
	}
	data = chomp(data, options.lineterminator);

	let cur = "", // The character we are currently processing.
		inQuote = false,
		fieldQuoted = false,
		field: string | number | null = "", // Buffer for building up the current field
		row: string[] = [],
		out: string[][] = [],
		i;

	function processField(field: string): string {
		let processedField: string | number | null = field;
		if (!fieldQuoted) {
			// If field is empty set to null
			if (field === "") {
				processedField = null;
				// If the field was not quoted and we are trimming fields, trim it
			} else if (options.skipinitialspace === true) {
				processedField = field.trim();
			}

			// Convert unquoted numbers to their appropriate types
			if (regexForInt.test(field)) {
				processedField = parseInt(field, 10);
			} else if (regexForFloat.test(field)) {
				processedField = parseFloat(field);
			}
		}
		return processedField?.toString() ?? "null";
	}

	for (i = 0; i < data.length; i += 1) {
		cur = data.charAt(i);

		// If we are at a EOF or EOR
		if (
			inQuote === false &&
			(cur === options.delimiter || cur === options.lineterminator)
		) {
			field = processField(field);
			// Add the current field to the current row
			row.push(field);
			// If this is EOR append row to output and flush row
			if (cur === options.lineterminator) {
				out.push(row);
				row = [];
			}
			// Flush the field buffer
			field = "";
			fieldQuoted = false;
		} else {
			// If it's not a quotechar, add it to the field buffer
			if (cur !== options.quotechar) {
				field += cur;
			} else {
				if (!inQuote) {
					// We are not in a quote, start a quote
					inQuote = true;
					fieldQuoted = true;
				} else {
					// Next char is quotechar, this is an escaped quotechar
					if (data.charAt(i + 1) === options.quotechar) {
						field += options.quotechar;
						// Skip the next char
						i += 1;
					} else {
						// It's not escaping, so end quote
						inQuote = false;
					}
				}
			}
		}
	}

	// Add the last field
	field = processField(field);
	row.push(field);
	out.push(row);

	// Expose the ability to discard initial rows
	if (options.skipinitialrows) out = out.slice(options.skipinitialrows);

	return out;
}
